import { useMemo } from 'react';
import type { ColumnDef, GaugeMapping, VisualizationConfig } from '@deckspace/types';
import { prepGaugeSeries } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: GaugeMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

interface GaugeGeometry {
  value: number;
  min: number;
  max: number;
  pct: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  largeArc: number;
  arcEndX: number;
  arcEndY: number;
}

const RADIUS = 70;
const CX = 80;
const CY = 80;

function computeGeometry(value: number, min: number, max: number): GaugeGeometry {
  const pct = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const angle = -90 + pct * 180;

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  return {
    value,
    min,
    max,
    pct,
    startX: CX + RADIUS * Math.cos(toRad(-90)),
    startY: CY + RADIUS * Math.sin(toRad(-90)),
    endX: CX + RADIUS * Math.cos(toRad(angle)),
    endY: CY + RADIUS * Math.sin(toRad(angle)),
    largeArc: pct > 0.5 ? 1 : 0,
    arcEndX: CX + RADIUS * Math.cos(toRad(90)),
    arcEndY: CY + RADIUS * Math.sin(toRad(90)),
  };
}

export function GaugeChart({ rows, columnDefs, mapping }: Props) {
  const geo = useMemo((): GaugeGeometry | null => {
    // Gauge's prep handles empty rows internally (returns 0/0/100).
    if (rows.length === 0) return null;
    const { gaugeValue, gaugeMin, gaugeMax } = prepGaugeSeries(rows, columnDefs, mapping);
    return computeGeometry(gaugeValue, gaugeMin, gaugeMax);
  }, [rows, columnDefs, mapping]);

  if (!geo) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No data available for gauge chart
      </div>
    );
  }

  const bgArcPath = `M ${geo.startX} ${geo.startY} A ${RADIUS} ${RADIUS} 0 1 1 ${geo.arcEndX} ${geo.arcEndY}`;
  const valueArcPath = `M ${geo.startX} ${geo.startY} A ${RADIUS} ${RADIUS} 0 ${geo.largeArc} 1 ${geo.endX} ${geo.endY}`;

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <svg width="160" height="100" viewBox="0 0 160 100">
        <path d={bgArcPath} fill="none" stroke="#e5e7eb" strokeWidth="12" strokeLinecap="round" />
        <path
          d={valueArcPath}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <text
          x={CX}
          y={CY - 5}
          textAnchor="middle"
          className="text-lg font-bold fill-zinc-700 dark:fill-zinc-300"
        >
          {geo.value}
        </text>
        <text x={CX} y={CY + 12} textAnchor="middle" className="text-[10px] fill-zinc-400">
          {geo.min} — {geo.max}
        </text>
      </svg>
    </div>
  );
}
